import { Show, For, createSignal, createMemo } from "solid-js";
import { A } from "@solidjs/router";
import { useQuizContext } from "./QuizContext";

export default function AdminQuizzes() {
  const [quizStore, _setQuizStore, quizActions] = useQuizContext() as any;
  const [showArchived, setShowArchived] = createSignal(false);
  const [showDeleted, setShowDeleted] = createSignal(false);

  const filteredQuizzes = createMemo(() => {
    return quizStore.quizzes.filter((quiz: any) => {
      if (quiz.state === 'archived' && !showArchived()) return false;
      if (quiz.state === 'deleted' && !showDeleted()) return false;
      return true;
    });
  });

  const handleCreate = async () => {
    const title = prompt("Quiz title:");
    if (!title?.trim()) return;
    
    const defaultMarkdown = `# Question 1

What is the capital of France?

:choice:
- London
X Paris
- Berlin

---

# Question 2

Select all that apply:

:choices:
- Option A
X Option B
X Option C
- Option D`;
    
    try {
      await quizActions.createQuiz({
        title: title.trim(),
        markdown: defaultMarkdown,
        state: 'hidden'
      });
    } catch (error: any) {
      alert('Error creating quiz: ' + (error.message || 'Unknown error'));
    }
  };

  const runQuizAction = async (operation: Promise<unknown>) => {
    try {
      await operation;
    } catch (error: any) {
      alert('Quiz operation failed: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDownloadCSV = async (quiz: any) => {
    try {
      await quizActions.downloadCSV(quiz.id, quiz.title);
    } catch (error: any) {
      alert('Error downloading CSV: ' + (error.message || 'Unknown error'));
    }
  };

  return (
    <div>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h2>Manage Quizzes</h2>
        <div>
          <button 
            type="button" 
            class={`btn btn-sm me-2 ${showArchived() ? 'btn-warning' : 'btn-outline-warning'}`}
            onClick={() => setShowArchived(!showArchived())}
          >
            {showArchived() ? 'Hide' : 'Show'} Archived
          </button>
          <button 
            type="button" 
            class={`btn btn-sm me-2 ${showDeleted() ? 'btn-danger' : 'btn-outline-danger'}`}
            onClick={() => setShowDeleted(!showDeleted())}
          >
            {showDeleted() ? 'Hide' : 'Show'} Deleted
          </button>
          <button 
            type="button" 
            class="btn btn-primary"
            onClick={handleCreate}
            disabled={quizStore.loading}
          >
            Create New Quiz
          </button>
        </div>
      </div>

      <Show when={quizStore.loading}>
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </Show>

      <table class="table table-striped table-hover">
        <thead>
          <tr>
            <th scope="col">Quiz Code</th>
            <th scope="col">Title</th>
            <th scope="col">State</th>
            <th scope="col">Updated</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          <For each={filteredQuizzes()}>
            {(quiz) => (
              <tr>
                <td><code>{quiz.quizId}</code></td>
                <td>{quiz.title}</td>
                <td>
                  <span class={`badge bg-${
                    quiz.state === 'visible' ? 'success' : 
                    quiz.state === 'hidden' ? 'secondary' : 
                    quiz.state === 'archived' ? 'warning' : 
                    'danger'
                  }`}>
                    {quiz.state}
                  </span>
                </td>
                <td>{new Date(quiz.updatedAt).toLocaleDateString()}</td>
                <td>
                  <Show when={quiz.state !== 'deleted'}>
                    <A href={`/quiz/${quiz.id}/edit`} class="btn btn-sm btn-outline-primary me-2">
                      Edit
                    </A>
                  </Show>
                  <Show when={quiz.state !== 'archived' && quiz.state !== 'deleted'}>
                    <A href={`/admin/live/${quiz.id}`} class="btn btn-sm btn-outline-success me-2">
                      Go Live
                    </A>
                  </Show>
                  <Show when={quiz.state !== 'deleted'}>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-outline-info me-2"
                      onClick={() => handleDownloadCSV(quiz)}
                      title="Download quiz results as CSV"
                    >
                      CSV
                    </button>
                  </Show>
                  <Show when={quiz.state === 'archived'}>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-outline-success me-2"
                      onClick={() => runQuizAction(quizActions.unarchiveQuiz(quiz.id))}
                    >
                      Unarchive
                    </button>
                  </Show>
                  <Show when={quiz.state !== 'archived' && quiz.state !== 'deleted'}>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-outline-warning me-2"
                      onClick={() => runQuizAction(quizActions.archiveQuiz(quiz.id))}
                    >
                      Archive
                    </button>
                  </Show>
                  <Show when={quiz.state !== 'deleted'}>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-outline-danger"
                      onClick={() => runQuizAction(quizActions.markQuizDeleted(quiz.id))}
                    >
                      Delete
                    </button>
                  </Show>
                  <Show when={quiz.state === 'deleted'}>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-outline-danger"
                      onClick={() => runQuizAction(quizActions.deleteQuiz(quiz.id))}
                    >
                      Delete Forever
                    </button>
                  </Show>
                </td>
              </tr>
            )}
          </For>
        </tbody>
      </table>

      <Show when={filteredQuizzes().length === 0 && !quizStore.loading}>
        <div class="alert alert-info">
          <Show when={quizStore.quizzes.length === 0} fallback={
            <span>No quizzes match the current filters.</span>
          }>
            No quizzes yet. Click "Create New Quiz" to get started.
          </Show>
        </div>
      </Show>
    </div>
  );
}
